import React, { useCallback, useEffect, useState } from "react";

import { useFlags } from "launchdarkly-react-client-sdk";
import _ from "lodash";
import toast from "react-hot-toast";
import { FiCopy } from "react-icons/fi";
import { usePopper } from "react-popper";
import styled from "styled-components";

import { IntegrationName } from "../../types";
import {
  coinfirmAdditionalInfo,
  merkleScienceAdditionaInfo,
  ellipticAdditionalInfo,
  chainalysisAdditionalInfo,
} from "../../utils/formatAdditionalInfo";
import IntegrationResult from "../IntegrationResult";

import { Button, ClickableText } from "~/components/Button";
import * as DetailList from "~/components/DetailList";
import { Box, Section, Padding } from "~/components/Elements";
import SkeletonDetailList from "~/components/SkeletonDetailList";
import {
  hasCredentials,
  addressInfoSearch,
  sanctionSearch,
  IntegrationIDs,
  SanctionSearchResponse,
} from "~/endpoints/integrations";
import { ChainalysisAddressInfoSearchResponseMetadata } from "~/endpoints/integrations/types/chainalysis";
import { Chainalysis2AddressInfoSearchResponseMetadata } from "~/endpoints/integrations/types/chainalysis2";
import { CoinfirmAddressInfoSearchResponseMetadata } from "~/endpoints/integrations/types/coinfirm";
import { CrystalAddressInfoSearchResponseMetadata } from "~/endpoints/integrations/types/crystal";
import { EllipticAddressInfoSearchResponseMetadata } from "~/endpoints/integrations/types/elliptic";
import { MerkleScienceAddressInfoSearchResponseMetadata } from "~/endpoints/integrations/types/merklescience";
import { getImageUrlFromS3Key } from "~/endpoints/resources/s3Uris";
import { SignedMessageProof, SignedMessageTypes } from "~/endpoints/transactions/types";
import { TransactionWrap, Direction } from "~/hooks/use-transaction";
import useCurrentViewerVaspDID from "~/hooks/useCurrentViewerVaspDID";
import { RISK_SCORE_MERKLESCIENCE_VALUE } from "~/templates/rules/AdvancedRules/static";

const EMPTY = "-";
const NO_ACTIVE_INTEGRATIONS = "Integration Results Not Available";

type Integration = {
  name: string;
  logoUrl: string;
  riskDisplayText: string;
  riskDate: Date;
};

type ErrorType = "originator" | "beneficiary";

const WarningLabel = styled.span`
  color: orange;
`;
const AssetText = styled.div`
  text-transform: uppercase;
`;
const FiatText = styled.div`
  color: #7e7e7e;
  margin-left: 4px;
`;

const IntegrationErrorsContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
`;

export const datetime = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  timeZoneName: "short",
});
export const formatterDate = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
});
export const formatterTime = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "numeric",
});
export const formatterDateTime = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  hour12: true,
  timeZoneName: "short",
});

type TransactionPartProps = {
  transaction?: TransactionWrap | null;
  direction?: Direction | undefined;
  requiredFields?: string[];
  warnings?: string[];
};

function roundOrNA(ellipticRiskScore: string | null): string {
  if (ellipticRiskScore === undefined || ellipticRiskScore === null) {
    return "N/A";
  } else if (ellipticRiskScore === "-1") {
    return "NULL";
  } else {
    return Math.round(parseFloat(ellipticRiskScore)).toString();
  }
}

const OptionalLabel = styled.span`
  font-weight: normal;
  text-transform: lowercase;
`;

function CheckCircle(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg width="1em" height="1em" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <mask id="prefix__a" fill="#fff">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M7.5 15a7.5 7.5 0 005.748-12.318l-6.437 9.845-3.647-4.558 1.171-.938L6.69 9.973l5.463-8.356A7.5 7.5 0 107.5 15z"
        />
      </mask>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.5 15a7.5 7.5 0 005.748-12.318l-6.437 9.845-3.647-4.558 1.171-.938L6.69 9.973l5.463-8.356A7.5 7.5 0 107.5 15z"
        fill="#8EED7F"
      />
      <path
        d="M13.248 2.682l.153-.129-.173-.206-.147.226.167.109zm-6.437 9.845l-.156.125.172.216.151-.232-.167-.109zM3.164 7.969l-.125-.157-.156.125.125.156.156-.124zm1.171-.938l.157-.124-.125-.157-.157.125.125.156zM6.69 9.973l-.157.125.173.215.151-.23-.167-.11zm5.463-8.356l.168.11.1-.154-.144-.113-.124.157zM14.8 7.5a7.3 7.3 0 01-7.3 7.3v.4a7.7 7.7 0 007.7-7.7h-.4zm-1.705-4.69A7.27 7.27 0 0114.8 7.5h.4a7.67 7.67 0 00-1.799-4.947l-.306.258zm-.014-.237l-6.437 9.845.334.218 6.437-9.845-.334-.218zm-6.114 9.829L3.32 7.844l-.312.25 3.647 4.558.312-.25zM3.29 8.125l1.171-.937-.25-.313-1.17.937.249.313zm.89-.969l2.353 2.942.313-.25-2.353-2.941-.313.25zm2.677 2.926l5.464-8.356-.335-.219L6.52 9.864l.335.218zM7.5.2c1.71 0 3.284.588 4.528 1.574l.248-.314A7.668 7.668 0 007.5-.2v.4zM.2 7.5A7.3 7.3 0 017.5.2v-.4A7.7 7.7 0 00-.2 7.5h.4zm7.3 7.3A7.3 7.3 0 01.2 7.5h-.4a7.7 7.7 0 007.7 7.7v-.4z"
        fill="#48E231"
        mask="url(#prefix__a)"
      />
    </svg>
  );
}

// TODO: move to component
const Tooltip = styled(Box)`
  min-width: 180px;
  padding: 0.2rem 0.6rem;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.3);

  #arrow,
  #arrow::before {
    position: absolute;
    /* box-shadow: 0 0 5px rgb(0 0 0 / 30%); */
    width: 8px;
    height: 8px;
    background: inherit;
  }

  #arrow {
    visibility: hidden;
  }

  #arrow::before {
    visibility: visible;
    content: "";
    transform: rotate(45deg);
  }

  &[data-popper-placement^="top"] > #arrow {
    bottom: -4px;
  }

  &[data-popper-placement^="bottom"] > #arrow {
    top: -4px;
  }

  &[data-popper-placement^="left"] > #arrow {
    right: -4px;
  }

  &[data-popper-placement^="right"] > #arrow {
    left: -4px;
  }
`;

const TooltipWrap = styled.div`
  #tooltip-reference + #tooltip-container {
    visibility: hidden;
  }
  #tooltip-reference:hover + #tooltip-container {
    visibility: visible;
  }
  #tooltip-reference + #tooltip-container:hover {
    visibility: visible;
  }
`;

const ItemLogo = styled.img`
  width: 1em;
  height: 1em;
  margin-right: 0.3rem;
`;

const DIRECTIONS: Record<string, string> = {
  ["incoming"]: "Incoming",
  ["outgoing"]: "Outgoing",
};
const RISK: Record<string, string> = {
  ["unknown"]: "UNKNOWN",
  ["lowRisk"]: "LOW",
  ["mediumRisk"]: "MEDIUM",
  ["highRisk"]: "HIGH",
  ["LOW"]: "LOW",
  ["MEDIUM"]: "MEDIUM",
  ["HIGH"]: "HIGH",
  ["SEVERE"]: "SEVERE",
  ["NO_ALERTS_AVAILABLE"]: "NO_ALERTS_AVAILABLE",
  ["NO_RISK"]: "NO RISK",
  ["CRITICAL"]: "CRITICAL",
};

type TooltipContentProps = {
  label?: string;
  logoUrl: string;
  integrationName: string;
  error?: boolean;
};

function IntegrationTooltipContent({
  label = "Verified by:",
  logoUrl,
  integrationName,
  error = false,
}: TooltipContentProps) {
  return (
    <>
      <p
        id="verifiedBy"
        style={{
          fontWeight: "bold",
          textTransform: "uppercase",
          fontSize: "12px",
          color: error ? "#ff4b4b" : "#41E029",
          display: "inline-block",
        }}
      >
        {label}
      </p>
      <ul>
        <p
          id={integrationName}
          style={{
            display: "inline-flex",
            alignItems: "center",
            fontWeight: "bold",
            fontSize: "12px",
          }}
        >
          <ItemLogo src={logoUrl} /> {integrationName}
        </p>
      </ul>
    </>
  );
}

const OwnershipProofContainer = styled.div`
  display: inline-flex;
  height: 26px;
  padding: 6px 11px;
  margin-top: 6px;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  border-radius: 20px;
  border: 1.5px solid #d2d2d2;
`;

const OwnershipProofText = styled.span<{ color?: string; display?: string }>`
  color: ${(props) => (props.color ? props.color : "#1f1f1f")};
  font-family: Montserrat;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  display: ${(props) => (props.display ? props.display : "inline")};
  cursor: pointer;
`;

const CheckMarkIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="16" viewBox="0 0 15 16" fill="none" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.9419 3.80806C13.186 4.05214 13.186 4.44786 12.9419 4.69194L6.06694 11.5669C5.82286 11.811 5.42714 11.811 5.18306 11.5669L2.05806 8.44194C1.81398 8.19786 1.81398 7.80214 2.05806 7.55806C2.30214 7.31398 2.69786 7.31398 2.94194 7.55806L5.625 10.2411L12.0581 3.80806C12.3021 3.56398 12.6979 3.56398 12.9419 3.80806Z"
      fill="#30A919"
    />
  </svg>
);

const ClockIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3ZM1 12C1 5.92487 5.92487 1 12 1C18.0751 1 23 5.92487 23 12C23 18.0751 18.0751 23 12 23C5.92487 23 1 18.0751 1 12ZM12 5C12.5523 5 13 5.44772 13 6V11.382L16.4472 13.1056C16.9412 13.3526 17.1414 13.9532 16.8944 14.4472C16.6474 14.9412 16.0468 15.1414 15.5528 14.8944L11.5528 12.8944C11.214 12.725 11 12.3788 11 12V6C11 5.44772 11.4477 5 12 5Z"
      fill="#30A919"
    />
  </svg>
);

export const VerticalDivider = styled.div`
  border-left: 1px solid #d2d2d2;
  border-right: 1px solid #d2d2d2;
  height: 17px;
`;

const OwnershipProof = ({ transaction }: { transaction: TransactionWrap }) => {
  const { beneficiaryProof } = transaction;

  if (
    beneficiaryProof?.type === SignedMessageTypes.CheckboxConfirmation ||
    beneficiaryProof?.type === SignedMessageTypes.SelfDeclarationConfirmation
  ) {
    return (
      <>
        <OwnershipProofContainer>
          <CheckMarkIcon />
          <VerticalDivider />
          <OwnershipProofText>Self Declared</OwnershipProofText>
        </OwnershipProofContainer>
        <OwnershipProofText color="#8E8E8E" display="block">
          Type: {beneficiaryProof?.type}
        </OwnershipProofText>
      </>
    );
  }

  if (!beneficiaryProof) {
    return null;
  }

  if (beneficiaryProof.type === "screenshot" && beneficiaryProof.s3Key) {
    return <ProofImageLoader s3Key={beneficiaryProof.s3Key} />;
  }

  const signedMessageProof = beneficiaryProof as SignedMessageProof;

  if (!signedMessageProof?.proof) {
    return null;
  }

  if (
    signedMessageProof.type &&
    [
      SignedMessageTypes.PersonalSignBIP137,
      SignedMessageTypes.PersonalSignEIP191,
      SignedMessageTypes.PersonalSignEIP712,
      SignedMessageTypes.PersonalSignXPUB,
    ].includes(signedMessageProof.type)
  ) {
    return (
      <>
        <OwnershipProofContainer>
          <CheckMarkIcon />
          <VerticalDivider />
          <OwnershipProofText
            color="#30A919"
            onClick={() => {
              navigator.clipboard.writeText(signedMessageProof?.proof || "");
              toast(`Ownership proof Copied to clipboard`, { duration: 21000 });
            }}
          >
            #
            {signedMessageProof.proof.length > 50
              ? `${signedMessageProof.proof.slice(0, 50)}...`
              : signedMessageProof.proof}
            <FiCopy title="Copy to clipboard" />
          </OwnershipProofText>
        </OwnershipProofContainer>
        <OwnershipProofText color="#8E8E8E" display="block">
          Type: {`Cryptographic signature ${transaction?.beneficiaryProof?.type}`}
        </OwnershipProofText>
      </>
    );
  }

  return (
    <>
      <OwnershipProofContainer>
        <ClockIcon />
        <VerticalDivider />
        <OwnershipProofText
          color="#30A919"
          onClick={() => {
            navigator.clipboard.writeText(signedMessageProof?.proof || "");
            toast(`Proof Copied to clipboard`, { duration: 2000 });
          }}
        >
          #
          {signedMessageProof.proof.length > 50
            ? `${signedMessageProof.proof.slice(0, 50)}...`
            : signedMessageProof.proof}
          <FiCopy title="Copy to clipboard" />
        </OwnershipProofText>
      </OwnershipProofContainer>
      <OwnershipProofText color="#8E8E8E" display="block">
        Type: {signedMessageProof?.type}
      </OwnershipProofText>
    </>
  );
};

export default function TransactionInformation({
  transaction,
  direction,
  warnings,
  ...props
}: TransactionPartProps & { direction?: string }) {
  const { showOwnershipProof } = useFlags();
  const vaspDID = useCurrentViewerVaspDID();
  const [hasElliptic, setHasElliptic] = useState(false);
  const [, setEllipticError] = useState("");
  const [hasTRM, setHasTRM] = useState(false);
  const [, setTRMError] = useState("");
  const [hasCoinfirm, setHasCoinfirm] = useState(false);
  const [, setCoinfirmError] = useState("");
  const [hasCrystal, setHasCrystal] = useState(false);
  const [, setCrystalError] = useState("");
  const [hasTRMSanctions, setHasTRMSanctions] = useState(false);
  const [trmSanctionsResult, setTRMSanctionsResult] = useState<SanctionSearchResponse | undefined>(undefined);
  const [hasChainalysis, setHasChainalysisApiCredentials] = useState(false);
  const [, setChainalysisError] = useState("");
  const [hasChainalysis2, setHasChainalysis2ApiCredentials] = useState(false);
  const [, setChainalysis2Error] = useState("");
  const [hasMerkleScience, setHasMerkleScienceApiCredentials] = useState(false);
  const [, setMerkleScienceError] = useState("");
  const [hasScorechain, setHasScorechain] = useState(false);
  const [, setScorechainError] = useState("");
  const [activeIntegrationsBeneficiary, setActiveIntegrationsBeneficiary] = useState<Integration[]>([]);
  const [activeIntegrationsOriginator, setActiveIntegrationsOriginator] = useState<Integration[]>([]);
  const [activeIntegrationsErrorsBeneficiary, setActiveIntegrationsErrorsBeneficiary] = useState<Integration[]>([]);
  const [activeIntegrationsErrorsOriginator, setActiveIntegrationsErrorsOriginator] = useState<Integration[]>([]);
  const [beneficiaryHasRisk, setBeneficiaryHasRisk] = useState(false);
  const [
    chainalysisBeneficiaryResponse,
    setChainalysisBeneficiaryResponse,
  ] = useState<ChainalysisAddressInfoSearchResponseMetadata>();
  const [
    chainalysis2BeneficiaryResponse,
    setChainalysis2BeneficiaryResponse,
  ] = useState<Chainalysis2AddressInfoSearchResponseMetadata>();
  const [
    coinfirmBeneficiaryResponse,
    setCoinfirmBeneficiaryResponse,
  ] = useState<CoinfirmAddressInfoSearchResponseMetadata>();
  const [
    crystalBeneficiaryResponse,
    setCrystalBeneficiaryResponse,
  ] = useState<CrystalAddressInfoSearchResponseMetadata>();
  const [
    merkleScienceBeneficiaryResponse,
    setMerkleScienceBeneficiaryResponse,
  ] = useState<MerkleScienceAddressInfoSearchResponseMetadata>();
  const [
    ellipticBeneficiaryResponse,
    setEllipticBeneficiaryResponse,
  ] = useState<EllipticAddressInfoSearchResponseMetadata>();
  const [
    chainalysisOriginatorResponse,
    setChainalysisOriginatorResponse,
  ] = useState<ChainalysisAddressInfoSearchResponseMetadata>();
  const [
    chainalysis2OriginatorResponse,
    setChainalysis2OriginatorResponse,
  ] = useState<Chainalysis2AddressInfoSearchResponseMetadata>();
  const [
    coinfirmOriginatorResponse,
    setCoinfirmOriginatorResponse,
  ] = useState<CoinfirmAddressInfoSearchResponseMetadata>();
  const [
    crystalOriginatorResponse,
    setCrystalOriginatorResponse,
  ] = useState<CrystalAddressInfoSearchResponseMetadata>();
  const [
    merkleScienceOriginatorResponse,
    setMerkleScienceOriginatorResponse,
  ] = useState<MerkleScienceAddressInfoSearchResponseMetadata>();
  const [
    ellipticOriginatorResponse,
    setEllipticOriginatorResponse,
  ] = useState<EllipticAddressInfoSearchResponseMetadata>();
  const [scorechainOriginatorResponse, setScorechainOriginatorResponse] = useState<any>(null);
  const [scorechainBeneficiaryResponse, setScorechainBeneficiaryResponse] = useState<any>(null);
  const isOutgoing = direction === "outgoing";
  const isIncoming = direction === "incoming";
  const fiatAmount = isOutgoing ? transaction?.amountInLocalCurrency : transaction?.beneficiaryAmountInLocalCurrency;

  useEffect(() => {
    async function checkForTRM() {
      if (vaspDID) {
        const trmExists = await hasCredentials({ vaspDID, integrationId: IntegrationIDs.TRM });
        setHasTRM(trmExists);
      }
    }

    checkForTRM();
  }, [vaspDID]);

  useEffect(() => {
    async function checkForCoinfirm() {
      if (vaspDID) {
        const coinfirmExists = await hasCredentials({ vaspDID, integrationId: IntegrationIDs.COINFIRM });
        setHasCoinfirm(coinfirmExists);
      }
    }

    checkForCoinfirm();
  }, [vaspDID]);

  useEffect(() => {
    async function checkForCrystal() {
      if (vaspDID) {
        const crystalExists = await hasCredentials({ vaspDID, integrationId: IntegrationIDs.CRYSTAL });
        setHasCrystal(crystalExists);
      }
    }

    checkForCrystal();
  }, [vaspDID]);

  useEffect(() => {
    async function checkForMerkleScience() {
      if (vaspDID) {
        const merkleScienceExists = await hasCredentials({ vaspDID, integrationId: IntegrationIDs.MERKLESCIENCE });
        setHasMerkleScienceApiCredentials(merkleScienceExists);
      }
    }

    checkForMerkleScience();
  }, [vaspDID]);

  useEffect(() => {
    async function checkForTRMSanctions() {
      if (vaspDID) {
        const trmSanctionsExists = await hasCredentials({ vaspDID, integrationId: IntegrationIDs.TRMSANCTIONS });
        setHasTRMSanctions(trmSanctionsExists);
      }
    }

    checkForTRMSanctions();
  }, [vaspDID]);

  useEffect(() => {
    async function checkForChainalysisKey() {
      if (vaspDID) {
        const hasChainalysisCredentialsResult = await hasCredentials({
          vaspDID,
          integrationId: IntegrationIDs.CHAINALYSIS,
        });
        setHasChainalysisApiCredentials(hasChainalysisCredentialsResult);
      }
    }

    checkForChainalysisKey();
  }, [vaspDID]);

  useEffect(() => {
    async function checkForChainalysis2Key() {
      if (vaspDID) {
        const hasChainalysis2CredentialsResult = await hasCredentials({
          vaspDID,
          integrationId: IntegrationIDs.CHAINALYSIS2,
        });
        setHasChainalysis2ApiCredentials(hasChainalysis2CredentialsResult);
      }
    }

    checkForChainalysis2Key();
  }, [vaspDID]);

  useEffect(() => {
    async function checkForEllipticCredentials() {
      if (vaspDID) {
        const hasEllipticApiCredentials = await hasCredentials({ vaspDID, integrationId: IntegrationIDs.ELLIPTIC });
        setHasElliptic(hasEllipticApiCredentials);
      }
    }

    checkForEllipticCredentials();
  }, [vaspDID]);

  useEffect(() => {
    async function checkForScorechain() {
      if (vaspDID) {
        const scorechainExists = await hasCredentials({ vaspDID, integrationId: IntegrationIDs.SCORECHAIN });
        setHasScorechain(scorechainExists);
      }
    }

    checkForScorechain();
  }, [vaspDID]);

  // Tooltip for originator // TODO: DRY code on cooldown
  const [popperRefElementOriginatorAdd, setPopperRefElementOriginatorAdd] = useState<any | null>(null);
  const [popperElementOriginatorAdd, setPopperElementOriginatorAdd] = useState<any | null>(null);
  const [popperArrowElementOriginatorAdd, setPopperArrowElementOriginatorAdd] = useState<any | null>(null);
  const { styles: popperStylesOriginatorAdd, attributes: popperAttrsOriginatorAdd } = usePopper(
    popperRefElementOriginatorAdd,
    popperElementOriginatorAdd,
    {
      modifiers: [
        { name: "arrow", options: { element: popperArrowElementOriginatorAdd } },
        { name: "offset", options: { offset: [0, 10] } },
      ],
      placement: "right",
    },
  );

  const [popperRefElementBeneficiaryAdd, setPopperRefElementBeneficiaryAdd] = useState<any | null>(null);
  const [popperElementBeneficiaryAdd, setPopperElementBeneficiaryAdd] = useState<any | null>(null);
  const [popperArrowElementBeneficiaryAdd, setPopperArrowElementBeneficiaryAdd] = useState<any | null>(null);
  const { styles: popperStylesBeneficiaryAdd, attributes: popperAttrsBeneficiaryAdd } = usePopper(
    popperRefElementBeneficiaryAdd,
    popperElementBeneficiaryAdd,
    {
      modifiers: [
        { name: "arrow", options: { element: popperArrowElementBeneficiaryAdd } },
        { name: "offset", options: { offset: [0, 10] } },
      ],
      placement: "right",
    },
  );

  const addActiveIntegrationBeneficiary = useCallback(
    (name: string, logoUrl: string, riskText: string, riskDate: Date) => {
      setActiveIntegrationsBeneficiary((prevState) => {
        if (prevState.findIndex((integration) => integration.name === name) === -1) {
          return [
            ...prevState,
            {
              name: name,
              logoUrl: logoUrl,
              riskDisplayText: riskText,
              riskDate,
            },
          ];
        }
        return prevState;
      });
    },
    [],
  );

  const addActiveIntegrationOriginator = useCallback(
    (name: string, logoUrl: string, riskText: string, riskDate: Date) => {
      setActiveIntegrationsOriginator((prevState) => {
        if (prevState.findIndex((integration) => integration.name === name) === -1) {
          return [
            ...prevState,
            {
              name: name,
              logoUrl: logoUrl,
              riskDisplayText: riskText,
              riskDate,
            },
          ];
        }
        return prevState;
      });
    },
    [],
  );

  const addActiveIntegrationErrorOriginator = useCallback(
    (name: string, logoUrl: string, errorText: string, riskDate: Date) => {
      setActiveIntegrationsErrorsOriginator((prevState) => {
        if (prevState.findIndex((integration) => integration.name === name) === -1) {
          return [
            ...prevState,
            {
              name: name,
              logoUrl: logoUrl,
              riskDisplayText: errorText,
              riskDate,
            },
          ];
        }
        return prevState;
      });
    },
    [],
  );

  const addActiveIntegrationErrorBeneficiary = useCallback(
    (name: string, logoUrl: string, errorText: string, riskDate: Date) => {
      setActiveIntegrationsErrorsBeneficiary((prevState) => {
        if (prevState.findIndex((integration) => integration.name === name) === -1) {
          return [
            ...prevState,
            {
              name: name,
              logoUrl: logoUrl,
              riskDisplayText: errorText,
              riskDate,
            },
          ];
        }
        return prevState;
      });
    },
    [],
  );

  const showIntegrationErrorChainalysis = useCallback(
    (errorType: ErrorType, errorMessage: string) =>
      setChainalysisError((currentError) => {
        const error = `${errorType}/${errorMessage}`;
        if (currentError === error || errorMessage === "error code 500: no hit") {
          // don't do anything if this error has already been displayed
          return currentError;
        }

        if (errorType === "beneficiary") {
          addActiveIntegrationErrorBeneficiary(
            IntegrationName.Chainalysis_V1,
            "/assets/chainalysis.png",
            errorMessage,
            new Date(),
          );
        } else {
          addActiveIntegrationErrorOriginator(
            IntegrationName.Chainalysis_V1,
            "/assets/chainalysis.png",
            errorMessage,
            new Date(),
          );
        }

        return error;
      }),
    [addActiveIntegrationErrorBeneficiary, addActiveIntegrationErrorOriginator],
  );

  const showIntegrationErrorChainalysis2 = useCallback(
    (errorType: ErrorType, errorMessage: string) =>
      setChainalysis2Error((currentError) => {
        const error = `${errorType}/${errorMessage}`;
        if (currentError === error || errorMessage === "error code 500: no hit") {
          // don't do anything if this error has already been displayed
          return currentError;
        }

        if (errorType === "beneficiary") {
          addActiveIntegrationErrorBeneficiary(
            IntegrationName.Chainalysis_V2,
            "/assets/chainalysis.png",
            errorMessage,
            new Date(),
          );
        } else {
          addActiveIntegrationErrorOriginator(
            IntegrationName.Chainalysis_V2,
            "/assets/chainalysis.png",
            errorMessage,
            new Date(),
          );
        }

        return error;
      }),
    [addActiveIntegrationErrorBeneficiary, addActiveIntegrationErrorOriginator],
  );

  const showIntegrationErrorCoinfirm = useCallback(
    (errorType: ErrorType, errorMessage: string) =>
      setCoinfirmError((currentError) => {
        const error = `${errorType}/${errorMessage}`;
        if (currentError === error || errorMessage === "error code 500: no hit") {
          // don't do anything if this error has already been displayed
          return currentError;
        }

        if (errorType === "beneficiary") {
          addActiveIntegrationErrorBeneficiary(
            IntegrationName.Coinfirm,
            "/assets/coinfirm.png",
            errorMessage,
            new Date(),
          );
        } else {
          addActiveIntegrationErrorOriginator(
            IntegrationName.Coinfirm,
            "/assets/coinfirm.png",
            errorMessage,
            new Date(),
          );
        }

        return error;
      }),
    [addActiveIntegrationErrorBeneficiary, addActiveIntegrationErrorOriginator],
  );

  const showIntegrationErrorCrystal = useCallback(
    (errorType: ErrorType, errorMessage: string) =>
      setCrystalError((currentError) => {
        const error = `${errorType}/${errorMessage}`;
        if (currentError === error) {
          // don't do anything if this error has already been displayed
          return currentError;
        }

        if (errorType === "beneficiary") {
          addActiveIntegrationErrorBeneficiary(
            IntegrationName.Crystal,
            "/assets/crystalBlockchain.svg",
            errorMessage,
            new Date(),
          );
        } else {
          addActiveIntegrationErrorOriginator(
            IntegrationName.Crystal,
            "/assets/crystalBlockchain.svg",
            errorMessage,
            new Date(),
          );
        }

        return error;
      }),
    [addActiveIntegrationErrorBeneficiary, addActiveIntegrationErrorOriginator],
  );

  const showIntegrationErrorTRM = useCallback(
    (errorType: ErrorType, errorMessage: string) =>
      setTRMError((currentError) => {
        const error = `${errorType}/${errorMessage}`;
        if (currentError === error || errorMessage === "error code 500: no hit") {
          // don't do anything if this error has already been displayed
          return currentError;
        }

        if (errorType === "beneficiary") {
          addActiveIntegrationErrorBeneficiary(IntegrationName.TRM, "/assets/trm.png", errorMessage, new Date());
        } else {
          addActiveIntegrationErrorOriginator(IntegrationName.TRM, "/assets/trm.png", errorMessage, new Date());
        }

        return error;
      }),
    [],
  );

  const showIntegrationErrorElliptic = useCallback(
    (errorType: ErrorType, errorMessage: string) =>
      setEllipticError((currentError) => {
        const error = `${errorType}/${errorMessage}`;
        if (currentError === error || errorMessage === "error code 500: no hit") {
          // don't do anything if this error has already been displayed
          return currentError;
        }

        if (errorType === "beneficiary") {
          addActiveIntegrationErrorBeneficiary(
            IntegrationName.Elliptic,
            "/assets/elliptic.png",
            errorMessage,
            new Date(),
          );
        } else {
          addActiveIntegrationErrorOriginator(
            IntegrationName.Elliptic,
            "/assets/elliptic.png",
            errorMessage,
            new Date(),
          );
        }

        return error;
      }),
    [addActiveIntegrationErrorBeneficiary, addActiveIntegrationErrorOriginator],
  );

  const showIntegrationErrorMerkleScience = useCallback(
    (errorType: ErrorType, errorMessage: string) =>
      setMerkleScienceError((currentError) => {
        const error = `${errorType}/${errorMessage}`;
        if (currentError === error || errorMessage === "error code 500: no hit") {
          // don't do anything if this error has already been displayed
          return currentError;
        }

        if (errorType === "beneficiary") {
          addActiveIntegrationErrorBeneficiary(
            IntegrationName.Merkle_Science,
            "/assets/merkleScience.png",
            errorMessage,
            new Date(),
          );
        } else {
          addActiveIntegrationErrorOriginator(
            IntegrationName.Merkle_Science,
            "/assets/merkleScience.png",
            errorMessage,
            new Date(),
          );
        }

        return error;
      }),
    [addActiveIntegrationErrorBeneficiary, addActiveIntegrationErrorOriginator],
  );

  const showIntegrationErrorTRMSanctions = useCallback(
    (errorType: ErrorType, errorMessage: string) =>
      setMerkleScienceError((currentError) => {
        const error = `${errorType}/${errorMessage}`;
        if (currentError === error || errorMessage === "error code 500: no hit") {
          // don't do anything if this error has already been displayed
          return currentError;
        }

        if (errorType === "beneficiary") {
          addActiveIntegrationErrorBeneficiary(
            IntegrationName.TRM_Sanctions,
            "/assets/trm.png",
            errorMessage,
            new Date(),
          );
        } else {
          addActiveIntegrationErrorOriginator(
            IntegrationName.TRM_Sanctions,
            "/assets/trm.png",
            errorMessage,
            new Date(),
          );
        }

        return error;
      }),
    [addActiveIntegrationErrorBeneficiary, addActiveIntegrationErrorOriginator],
  );

  const showIntegrationErrorScorechain = useCallback(
    (errorType: ErrorType, errorMessage: string) =>
      setScorechainError((currentError) => {
        const error = `${errorType}/${errorMessage}`;
        if (currentError === error || errorMessage === "error code 500: no hit") {
          // don't do anything if this error has already been displayed
          return currentError;
        }

        if (errorType === "beneficiary") {
          addActiveIntegrationErrorBeneficiary(
            IntegrationName.Scorechain,
            "/assets/scorechain.png",
            errorMessage,
            new Date(),
          );
        } else {
          addActiveIntegrationErrorOriginator(
            IntegrationName.Scorechain,
            "/assets/scorechain.png",
            errorMessage,
            new Date(),
          );
        }

        return error;
      }),
    [addActiveIntegrationErrorBeneficiary, addActiveIntegrationErrorOriginator],
  );

  const transactionId = transaction?.id;
  const originatorBlockchainAddress = transaction?.originatorBlockchainAddress;
  const beneficiaryBlockchainAddress = transaction?.beneficiaryBlockchainAddress;
  const beneficiaryCustomerRef = transaction?.beneficiaryRef;
  const originatorCustomerRef = transaction?.originatorRef;
  const originatorDID = transaction?.originatorDid;
  const beneficiaryDID = transaction?.beneficiaryDid;
  const transactionAsset = transaction?.transactionAsset;
  const transactionAmount = transaction?.transactionAmount;

  const chainalysisCheck = useCallback(async () => {
    if (!hasChainalysis || !transactionAsset || !vaspDID) {
      return;
    }

    if (isOutgoing && beneficiaryBlockchainAddress) {
      try {
        const [chainalysisAddressInfoResponse] = await Promise.all([
          addressInfoSearch({
            vaspDID,
            integrationId: IntegrationIDs.CHAINALYSIS,
            data: {
              address: beneficiaryBlockchainAddress,
              asset: transactionAsset,
              transactionId,
              ...(originatorDID && { customerDID: originatorDID }),
            },
          }),
        ]);

        setChainalysisBeneficiaryResponse(chainalysisAddressInfoResponse?.metadata);

        if (
          chainalysisAddressInfoResponse?.score !== undefined &&
          RISK[chainalysisAddressInfoResponse.score] !== undefined
        ) {
          //TODO: use this id and logos for displaying the error as well. Maybe could be the same array, just with isError: true?
          addActiveIntegrationBeneficiary(
            IntegrationName.Chainalysis_V1,
            "/assets/chainalysis.png",
            RISK[chainalysisAddressInfoResponse.score],
            chainalysisAddressInfoResponse.createdAt,
          );
          setBeneficiaryHasRisk(true);
        }
      } catch (err) {
        showIntegrationErrorChainalysis("beneficiary", (err as Error).message);
      }
    } else if (isIncoming && originatorBlockchainAddress) {
      try {
        const [chainalysisAddressInfoResponse] = await Promise.all([
          addressInfoSearch({
            vaspDID,
            integrationId: IntegrationIDs.CHAINALYSIS,
            data: {
              address: originatorBlockchainAddress,
              asset: transactionAsset,
              transactionId,
              ...(beneficiaryDID && { customerDID: beneficiaryDID }),
            },
          }),
        ]);

        setChainalysisOriginatorResponse(chainalysisAddressInfoResponse?.metadata);

        if (
          chainalysisAddressInfoResponse?.score !== undefined &&
          RISK[chainalysisAddressInfoResponse.score] !== undefined
        ) {
          addActiveIntegrationOriginator(
            IntegrationName.Chainalysis_V1,
            "/assets/chainalysis.png",
            RISK[chainalysisAddressInfoResponse?.score],
            chainalysisAddressInfoResponse.createdAt,
          );
        }
      } catch (err) {
        showIntegrationErrorChainalysis("originator", (err as Error).message);
      }
    }
  }, [
    hasChainalysis,
    transactionAsset,
    vaspDID,
    isOutgoing,
    beneficiaryBlockchainAddress,
    isIncoming,
    originatorBlockchainAddress,
    originatorDID,
    addActiveIntegrationBeneficiary,
    showIntegrationErrorChainalysis,
    beneficiaryDID,
    addActiveIntegrationOriginator,
    transactionId,
  ]);

  useEffect(() => {
    chainalysisCheck();
  }, [chainalysisCheck]);

  const chainalysis2Check = useCallback(async () => {
    if (!hasChainalysis2 || !transactionAsset || !transactionAmount || !vaspDID) {
      return;
    }

    if (isOutgoing && beneficiaryBlockchainAddress) {
      try {
        const [chainalysis2AddressInfoResponse] = await Promise.all([
          addressInfoSearch({
            vaspDID,
            integrationId: IntegrationIDs.CHAINALYSIS2,
            data: {
              address: beneficiaryBlockchainAddress,
              asset: transactionAsset,
              assetAmount: transactionAmount,
              transactionId,
              ...(originatorDID && { customerDID: originatorDID }),
            },
          }),
        ]);

        setChainalysis2BeneficiaryResponse(chainalysis2AddressInfoResponse?.metadata);

        if (
          chainalysis2AddressInfoResponse?.score !== undefined &&
          RISK[chainalysis2AddressInfoResponse.score] !== undefined
        ) {
          addActiveIntegrationBeneficiary(
            IntegrationName.Chainalysis_V2,
            "/assets/chainalysis.png",
            RISK[chainalysis2AddressInfoResponse.score],
            chainalysis2AddressInfoResponse.createdAt,
          );
          setBeneficiaryHasRisk(true);
        }
      } catch (err) {
        showIntegrationErrorChainalysis2("beneficiary", (err as Error).message);
      }
    } else if (isIncoming && originatorBlockchainAddress) {
      try {
        const [chainalysis2AddressInfoResponse] = await Promise.all([
          addressInfoSearch({
            vaspDID,
            integrationId: IntegrationIDs.CHAINALYSIS2,
            data: {
              address: originatorBlockchainAddress,
              asset: transactionAsset,
              assetAmount: transactionAmount,
              transactionId,
              ...(beneficiaryDID && { customerDID: beneficiaryDID }),
            },
          }),
        ]);

        setChainalysis2OriginatorResponse(chainalysis2AddressInfoResponse?.metadata);

        if (
          chainalysis2AddressInfoResponse?.score !== undefined &&
          RISK[chainalysis2AddressInfoResponse.score] !== undefined
        ) {
          addActiveIntegrationOriginator(
            IntegrationName.Chainalysis_V2,
            "/assets/chainalysis.png",
            RISK[chainalysis2AddressInfoResponse?.score],
            chainalysis2AddressInfoResponse.createdAt,
          );
        }
      } catch (err) {
        showIntegrationErrorChainalysis2("originator", (err as Error).message);
      }
    }
  }, [
    hasChainalysis2,
    transactionAsset,
    transactionAmount,
    vaspDID,
    isOutgoing,
    beneficiaryBlockchainAddress,
    isIncoming,
    originatorBlockchainAddress,
    originatorDID,
    addActiveIntegrationBeneficiary,
    showIntegrationErrorChainalysis2,
    beneficiaryDID,
    addActiveIntegrationOriginator,
    transactionId,
  ]);

  useEffect(() => {
    chainalysis2Check();
  }, [chainalysis2Check]);

  const ellipticCheck = useCallback(async () => {
    if (!hasElliptic || !transactionAsset || !vaspDID) {
      return;
    }

    if (isOutgoing && beneficiaryBlockchainAddress) {
      try {
        const [ellipticAddressInfoResponse] = await Promise.all([
          addressInfoSearch({
            vaspDID,
            integrationId: IntegrationIDs.ELLIPTIC,
            data: {
              address: beneficiaryBlockchainAddress,
              asset: transactionAsset,
              transactionId,
              ...(originatorDID && { customerDID: originatorDID }),
            },
          }),
        ]);

        setEllipticBeneficiaryResponse(ellipticAddressInfoResponse?.metadata);

        const elipticResponse = roundOrNA(ellipticAddressInfoResponse?.score ?? null);

        addActiveIntegrationBeneficiary(
          IntegrationName.Elliptic,
          "/assets/elliptic.png",
          elipticResponse,
          ellipticAddressInfoResponse.createdAt,
        );
        setBeneficiaryHasRisk(true);
      } catch (err) {
        showIntegrationErrorElliptic("beneficiary", (err as Error).message);
      }
    } else if (isIncoming && originatorBlockchainAddress) {
      try {
        const [ellipticAddressInfoResponse] = await Promise.all([
          addressInfoSearch({
            vaspDID,
            integrationId: IntegrationIDs.ELLIPTIC,
            data: {
              address: originatorBlockchainAddress,
              asset: transactionAsset,
              transactionId,
              ...(beneficiaryDID && { customerDID: beneficiaryDID }),
            },
          }),
        ]);

        setEllipticOriginatorResponse(ellipticAddressInfoResponse?.metadata);

        const elipticResponse = roundOrNA(ellipticAddressInfoResponse?.score ?? null);

        addActiveIntegrationOriginator(
          IntegrationName.Elliptic,
          "/assets/elliptic.png",
          elipticResponse,
          ellipticAddressInfoResponse.createdAt,
        );
      } catch (err) {
        showIntegrationErrorElliptic("originator", (err as Error).message);
      }
    }
  }, [
    hasElliptic,
    transactionAsset,
    vaspDID,
    isOutgoing,
    beneficiaryBlockchainAddress,
    isIncoming,
    originatorBlockchainAddress,
    originatorDID,
    addActiveIntegrationBeneficiary,
    showIntegrationErrorElliptic,
    beneficiaryDID,
    addActiveIntegrationOriginator,
    transactionId,
  ]);

  useEffect(() => {
    ellipticCheck();
  }, [ellipticCheck]);

  const trmCheck = useCallback(async () => {
    if (!hasTRM || !transactionAsset || !vaspDID) {
      return;
    }

    if (isOutgoing && beneficiaryBlockchainAddress) {
      try {
        const trmAddressRiskResponse = await addressInfoSearch({
          vaspDID,
          integrationId: IntegrationIDs.TRM,
          data: {
            address: beneficiaryBlockchainAddress,
            asset: transactionAsset,
            transactionId,
            ...(beneficiaryCustomerRef && { customerRef: beneficiaryCustomerRef }),
          },
        });

        const trmResponse =
          trmAddressRiskResponse?.score !== "null" ? trmAddressRiskResponse?.score : "No/Unknown risk";

        if (trmResponse) {
          addActiveIntegrationBeneficiary(
            IntegrationName.TRM,
            "/assets/trm.png",
            trmResponse,
            trmAddressRiskResponse.createdAt,
          );
          setBeneficiaryHasRisk(true);
        }
      } catch (err) {
        showIntegrationErrorTRM("beneficiary", (err as Error).message);
      }
    } else if (isIncoming && originatorBlockchainAddress) {
      try {
        const trmAddressRiskResponse = await addressInfoSearch({
          vaspDID,
          integrationId: IntegrationIDs.TRM,
          data: {
            address: originatorBlockchainAddress,
            asset: transactionAsset,
            transactionId,
            ...(originatorCustomerRef && { customerRef: originatorCustomerRef }),
          },
        });

        const trmResponse = trmAddressRiskResponse.score !== "null" ? trmAddressRiskResponse.score : "No/Unknown risk";

        if (trmResponse) {
          addActiveIntegrationOriginator(
            IntegrationName.TRM,
            "/assets/trm.png",
            trmResponse,
            trmAddressRiskResponse.createdAt,
          );
        }
      } catch (err) {
        showIntegrationErrorTRM("originator", (err as Error).message);
      }
    }
  }, [
    hasTRM,
    transactionAsset,
    vaspDID,
    isOutgoing,
    beneficiaryBlockchainAddress,
    isIncoming,
    originatorBlockchainAddress,
    beneficiaryCustomerRef,
    addActiveIntegrationBeneficiary,
    showIntegrationErrorTRM,
    originatorCustomerRef,
    addActiveIntegrationOriginator,
    transactionId,
  ]);

  useEffect(() => {
    trmCheck();
  }, [trmCheck]);

  const merkleScienceCheck = useCallback(async () => {
    if (!hasMerkleScience || !transactionAsset || !vaspDID) {
      return;
    }

    if (isIncoming && originatorBlockchainAddress) {
      try {
        const [merkleScienceAddressInfoResponse] = await Promise.all([
          addressInfoSearch({
            vaspDID,
            integrationId: IntegrationIDs.MERKLESCIENCE,
            data: {
              address: originatorBlockchainAddress,
              asset: transactionAsset,
              transactionId,
            },
          }),
        ]);

        setMerkleScienceOriginatorResponse(merkleScienceAddressInfoResponse?.metadata);

        if (merkleScienceAddressInfoResponse?.score) {
          const score = parseInt(merkleScienceAddressInfoResponse?.score);
          if (!isNaN(score) && score < 6) {
            addActiveIntegrationOriginator(
              IntegrationName.Merkle_Science,
              "/assets/merkleScience.png",
              `${RISK_SCORE_MERKLESCIENCE_VALUE[score].label}: ${score}`,
              merkleScienceAddressInfoResponse.createdAt,
            );
          }
        }
      } catch (err) {
        showIntegrationErrorMerkleScience("originator", (err as Error).message);
      }
    } else if (isOutgoing && beneficiaryBlockchainAddress) {
      try {
        const [merkleScienceAddressInfoResponse] = await Promise.all([
          addressInfoSearch({
            vaspDID,
            integrationId: IntegrationIDs.MERKLESCIENCE,
            data: {
              address: beneficiaryBlockchainAddress,
              asset: transactionAsset,
              transactionId,
            },
          }),
        ]);

        setMerkleScienceBeneficiaryResponse(merkleScienceAddressInfoResponse?.metadata);

        if (merkleScienceAddressInfoResponse?.score) {
          const score = parseInt(merkleScienceAddressInfoResponse?.score);
          if (!isNaN(score) && score < 6) {
            addActiveIntegrationBeneficiary(
              IntegrationName.Merkle_Science,
              "/assets/merkleScience.png",
              `${RISK_SCORE_MERKLESCIENCE_VALUE[score].label}: ${score}`,
              merkleScienceAddressInfoResponse.createdAt,
            );
            setBeneficiaryHasRisk(true);
          }
        }
      } catch (err) {
        showIntegrationErrorMerkleScience("beneficiary", (err as Error).message);
      }
    }
  }, [
    hasMerkleScience,
    transactionAsset,
    vaspDID,
    isIncoming,
    originatorBlockchainAddress,
    isOutgoing,
    beneficiaryBlockchainAddress,
    addActiveIntegrationOriginator,
    showIntegrationErrorMerkleScience,
    addActiveIntegrationBeneficiary,
    transactionId,
  ]);

  useEffect(() => {
    merkleScienceCheck();
  }, [merkleScienceCheck]);

  const coinfirmCheck = useCallback(async () => {
    if (!hasCoinfirm || !vaspDID) {
      return;
    }

    if (isIncoming && originatorBlockchainAddress) {
      try {
        const [coinfirmAddressInfoResponse] = await Promise.all([
          addressInfoSearch({
            vaspDID,
            integrationId: IntegrationIDs.COINFIRM,
            data: {
              address: originatorBlockchainAddress,
              asset: transactionAsset,
              transactionId,
            },
          }),
        ]);

        setCoinfirmOriginatorResponse(coinfirmAddressInfoResponse?.metadata);

        if (coinfirmAddressInfoResponse?.score) {
          const coinfirmRiskScore = parseFloat(coinfirmAddressInfoResponse?.score);
          let originatorTextToShow = "";
          if (coinfirmRiskScore > 32 && coinfirmRiskScore <= 65) {
            originatorTextToShow = `MEDIUM ${coinfirmRiskScore}`;
          } else if (coinfirmRiskScore > 65) {
            originatorTextToShow = `HIGH ${coinfirmRiskScore}`;
          } else {
            originatorTextToShow = `LOW ${coinfirmRiskScore}`;
          }

          if (originatorTextToShow !== "" && !isNaN(coinfirmRiskScore)) {
            addActiveIntegrationOriginator(
              IntegrationName.Coinfirm,
              "/assets/coinfirm.png",
              originatorTextToShow,
              coinfirmAddressInfoResponse.createdAt,
            );
          }
        }
      } catch (err) {
        showIntegrationErrorCoinfirm("originator", (err as Error).message);
      }
    } else if (isOutgoing && beneficiaryBlockchainAddress) {
      try {
        const [coinfirmAddressInfoResponse] = await Promise.all([
          addressInfoSearch({
            vaspDID,
            integrationId: IntegrationIDs.COINFIRM,
            data: {
              address: beneficiaryBlockchainAddress,
              asset: transactionAsset,
              transactionId,
            },
          }),
        ]);

        setCoinfirmBeneficiaryResponse(coinfirmAddressInfoResponse?.metadata);

        if (coinfirmAddressInfoResponse?.score) {
          const coinfirmRiskScore = parseFloat(coinfirmAddressInfoResponse?.score);
          let beneficiaryTextToShow = "";
          if (coinfirmRiskScore > 32 && coinfirmRiskScore <= 65) {
            beneficiaryTextToShow = `MEDIUM ${coinfirmRiskScore}`;
          } else if (coinfirmRiskScore > 65) {
            beneficiaryTextToShow = `HIGH ${coinfirmRiskScore}`;
          } else {
            beneficiaryTextToShow = `LOW ${coinfirmRiskScore}`;
          }
          if (beneficiaryTextToShow !== "" && !isNaN(coinfirmRiskScore)) {
            addActiveIntegrationBeneficiary(
              IntegrationName.Coinfirm,
              "/assets/coinfirm.png",
              beneficiaryTextToShow,
              coinfirmAddressInfoResponse.createdAt,
            );
            setBeneficiaryHasRisk(true);
          }
        }
      } catch (err) {
        showIntegrationErrorCoinfirm("beneficiary", (err as Error).message);
      }
    }
  }, [
    hasCoinfirm,
    vaspDID,
    isIncoming,
    originatorBlockchainAddress,
    isOutgoing,
    beneficiaryBlockchainAddress,
    addActiveIntegrationOriginator,
    showIntegrationErrorCoinfirm,
    addActiveIntegrationBeneficiary,
    transactionAsset,
    transactionId,
  ]);

  useEffect(() => {
    coinfirmCheck();
  }, [coinfirmCheck]);

  const crystalCheck = useCallback(async () => {
    if (!hasCrystal || !vaspDID) {
      return;
    }

    if (isIncoming && originatorBlockchainAddress) {
      try {
        const [crystalAddressInfoResponse] = await Promise.all([
          addressInfoSearch({
            vaspDID,
            integrationId: IntegrationIDs.CRYSTAL,
            data: {
              address: originatorBlockchainAddress,
              asset: transactionAsset,
              transactionId,
            },
          }),
        ]);

        setCrystalOriginatorResponse(crystalAddressInfoResponse?.metadata);

        if (crystalAddressInfoResponse?.score) {
          const crystalRiskScore = parseFloat(crystalAddressInfoResponse?.score);
          let originatorTextToShow = "";
          if (crystalRiskScore >= 0.26 && crystalRiskScore <= 0.74) {
            originatorTextToShow = `MEDIUM ${crystalRiskScore}`;
          } else if (crystalRiskScore >= 0.75) {
            originatorTextToShow = `HIGH ${crystalRiskScore}`;
          } else {
            originatorTextToShow = `LOW ${crystalRiskScore}`;
          }

          if (originatorTextToShow !== "" && !isNaN(crystalRiskScore)) {
            addActiveIntegrationOriginator(
              IntegrationName.Crystal,
              "/assets/crystalBlockchain.svg",
              originatorTextToShow,
              crystalAddressInfoResponse.createdAt,
            );
          }
        }
      } catch (err) {
        showIntegrationErrorCrystal("originator", (err as Error).message);
      }
    } else if (isOutgoing && beneficiaryBlockchainAddress) {
      try {
        const [crystalAddressInfoResponse] = await Promise.all([
          addressInfoSearch({
            vaspDID,
            integrationId: IntegrationIDs.CRYSTAL,
            data: {
              address: beneficiaryBlockchainAddress,
              asset: transactionAsset,
              transactionId,
            },
          }),
        ]);

        setCrystalBeneficiaryResponse(crystalAddressInfoResponse?.metadata);

        if (crystalAddressInfoResponse?.score) {
          const crystalRiskScore = parseFloat(crystalAddressInfoResponse?.score);
          let beneficiaryTextToShow = "";
          if (crystalRiskScore > 0.25 && crystalRiskScore <= 0.75) {
            beneficiaryTextToShow = `MEDIUM ${crystalRiskScore}`;
          } else if (crystalRiskScore > 0.76) {
            beneficiaryTextToShow = `HIGH ${crystalRiskScore}`;
          } else {
            beneficiaryTextToShow = `LOW ${crystalRiskScore}`;
          }
          if (beneficiaryTextToShow !== "" && !isNaN(crystalRiskScore)) {
            addActiveIntegrationBeneficiary(
              IntegrationName.Crystal,
              "/assets/crystalBlockchain.svg",
              beneficiaryTextToShow,
              crystalAddressInfoResponse.createdAt,
            );
            setBeneficiaryHasRisk(true);
          }
        }
      } catch (err) {
        showIntegrationErrorCrystal("beneficiary", (err as Error).message);
      }
    }
  }, [
    hasCrystal,
    vaspDID,
    isIncoming,
    originatorBlockchainAddress,
    isOutgoing,
    beneficiaryBlockchainAddress,
    addActiveIntegrationOriginator,
    showIntegrationErrorCrystal,
    addActiveIntegrationBeneficiary,
    transactionAsset,
    transactionId,
  ]);

  useEffect(() => {
    crystalCheck();
  }, [crystalCheck]);

  const trmSanctionsCheck = useCallback(async () => {
    if (
      !transactionId ||
      (isOutgoing && !beneficiaryBlockchainAddress) ||
      (isIncoming && !originatorBlockchainAddress) ||
      !vaspDID ||
      !hasTRMSanctions
    ) {
      return;
    }

    try {
      const address = isIncoming ? originatorBlockchainAddress : beneficiaryBlockchainAddress;
      const screeningCheck = await sanctionSearch({
        vaspDID,
        integrationId: IntegrationIDs.TRMSANCTIONS,
        data: { searchTerm: address || "", transactionId },
      });
      setTRMSanctionsResult(screeningCheck);
    } catch (err) {
      showIntegrationErrorTRMSanctions(isIncoming ? "originator" : "beneficiary", (err as Error).message);
    }
  }, [
    beneficiaryBlockchainAddress,
    hasTRMSanctions,
    isIncoming,
    isOutgoing,
    originatorBlockchainAddress,
    showIntegrationErrorTRMSanctions,
    transactionId,
    vaspDID,
  ]);

  useEffect(() => {
    trmSanctionsCheck();
  }, [trmSanctionsCheck]);

  // Add this helper function at the top with other utility functions
  const getScoreChainRiskLevel = (score: number): string => {
    if (score === 1) return "CRITICAL";
    if (score >= 2 && score <= 29) return "HIGH";
    if (score >= 30 && score <= 69) return "MEDIUM";
    if (score >= 70 && score <= 99) return "LOW";
    if (score === 100) return "NO_RISK";
    return "NO_RISK"; // Default case
  };

  // Then modify the scorechainCheck to use this function
  const scorechainCheck = useCallback(async () => {
    if (!hasScorechain || !transactionAsset || !vaspDID) {
      return;
    }

    if (isOutgoing && beneficiaryBlockchainAddress) {
      try {
        const [scorechainAddressInfoResponse] = await Promise.all([
          addressInfoSearch({
            vaspDID,
            integrationId: IntegrationIDs.SCORECHAIN,
            data: {
              address: beneficiaryBlockchainAddress,
              asset: transactionAsset,
              transactionId,
              ...(originatorDID && { customerDID: originatorDID }),
            },
          }),
        ]);

        setScorechainBeneficiaryResponse(scorechainAddressInfoResponse?.metadata);

        if (scorechainAddressInfoResponse?.score) {
          const score = parseInt(scorechainAddressInfoResponse.score);
          const riskLevel = getScoreChainRiskLevel(score);

          addActiveIntegrationBeneficiary(
            IntegrationName.Scorechain,
            "/assets/scorechain.png",
            `${RISK[riskLevel]}: ${score}`,
            scorechainAddressInfoResponse.createdAt,
          );
          setBeneficiaryHasRisk(true);
        }
      } catch (err) {
        showIntegrationErrorScorechain("beneficiary", (err as Error).message);
      }
    } else if (isIncoming && originatorBlockchainAddress) {
      try {
        const [scorechainAddressInfoResponse] = await Promise.all([
          addressInfoSearch({
            vaspDID,
            integrationId: IntegrationIDs.SCORECHAIN,
            data: {
              address: originatorBlockchainAddress,
              asset: transactionAsset,
              transactionId,
              ...(beneficiaryDID && { customerDID: beneficiaryDID }),
            },
          }),
        ]);

        setScorechainOriginatorResponse(scorechainAddressInfoResponse?.metadata);

        if (scorechainAddressInfoResponse?.score) {
          const score = parseInt(scorechainAddressInfoResponse.score);
          const riskLevel = getScoreChainRiskLevel(score);

          addActiveIntegrationOriginator(
            IntegrationName.Scorechain,
            "/assets/scorechain.png",
            `${RISK[riskLevel]}: ${score}`,
            scorechainAddressInfoResponse.createdAt,
          );
        }
      } catch (err) {
        showIntegrationErrorScorechain("originator", (err as Error).message);
      }
    }
  }, [
    hasScorechain,
    transactionAsset,
    vaspDID,
    isOutgoing,
    isIncoming,
    beneficiaryBlockchainAddress,
    originatorBlockchainAddress,
    transactionId,
    originatorDID,
    beneficiaryDID,
    addActiveIntegrationBeneficiary,
    addActiveIntegrationOriginator,
    setBeneficiaryHasRisk,
    showIntegrationErrorScorechain,
  ]);

  useEffect(() => {
    chainalysisCheck();
    chainalysis2Check();
    merkleScienceCheck();
    coinfirmCheck();
    crystalCheck();
    scorechainCheck();
  }, [chainalysisCheck, chainalysis2Check, merkleScienceCheck, coinfirmCheck, crystalCheck, scorechainCheck]);

  return (
    <Box as={Section} {...props} data-intercom-target="blockchain-transaction-information-section-tourid-447932">
      <Padding sized="medium">
        <DetailList.Header>
          <DetailList.Item mobile={12}>
            <DetailList.Title as="h2">Blockchain Transaction information</DetailList.Title>
          </DetailList.Item>
        </DetailList.Header>
        {transaction ? (
          <DetailList.Content>
            {[
              {
                label: "Type",
                value: direction ? DIRECTIONS[direction] : direction,
              },
              {
                label: "Amount",
                value: (
                  <div style={{ display: "flex" }}>
                    <AssetText>{transaction.transactionAmountWithDecimals}</AssetText>
                    {fiatAmount && <FiatText>/ {fiatAmount?.amountInLocalCurrency}</FiatText>}
                  </div>
                ),
              },
              {
                label: "Asset",
                value: (
                  <div style={{ display: "flex" }}>
                    <AssetText>{transaction.transactionAsset}</AssetText>
                    {fiatAmount && <FiatText>/ {fiatAmount?.currency}</FiatText>}
                  </div>
                ),
              },
            ].map(({ label, value }) => (
              <DetailList.Item key={label} mobile={12} tablet={6} desktop={3}>
                <DetailList.Term>{label}</DetailList.Term>
                <DetailList.Description className="localize-ignore">{value || EMPTY}</DetailList.Description>
              </DetailList.Item>
            ))}
            {[
              {
                label: "Transaction Hash",
                value: transaction.transactionHash || EMPTY,
              },
              {},
              {
                label: "From Blockchain address",
                value: transaction.originatorBlockchainAddress || EMPTY,
                warning: "originatorAccountNumberMissing",
              },
              isIncoming
                ? {
                    label: "Risk score Result",
                    value:
                      activeIntegrationsOriginator && activeIntegrationsOriginator.length
                        ? activeIntegrationsOriginator.map((integration, index) => (
                            <IntegrationResult
                              key={index}
                              longLabel="TRANSACTION RISK"
                              provider={integration.name.toUpperCase()}
                              value={integration.riskDisplayText}
                              date={integration.riskDate}
                              isHighAlert={
                                integration.riskDisplayText === "HIGH" || integration.riskDisplayText === "SEVERE"
                              }
                              logo={integration.logoUrl}
                            />
                          ))
                        : NO_ACTIVE_INTEGRATIONS,
                  }
                : {},
              isIncoming && activeIntegrationsErrorsOriginator && activeIntegrationsErrorsOriginator.length
                ? {
                    label: "Risk score Errors",
                    value: (
                      <IntegrationErrorsContainer>
                        {activeIntegrationsErrorsOriginator.map((integration, index) => (
                          <IntegrationResult
                            key={index}
                            longLabel="TRANSACTION RISK ERRORS"
                            provider={integration.name}
                            value={integration.riskDisplayText}
                            date={integration.riskDate}
                            isHighAlert={
                              integration.riskDisplayText === "HIGH" || integration.riskDisplayText === "SEVERE"
                            }
                            logo={integration.logoUrl}
                          />
                        ))}
                      </IntegrationErrorsContainer>
                    ),
                  }
                : {},
              isIncoming
                ? {
                    label: "Counterparty Address Sanctioned?",
                    value:
                      hasTRMSanctions && trmSanctionsResult ? (
                        <IntegrationResult
                          shortLabel="MATCHES"
                          longLabel="SANCTION MATCHES"
                          provider={IntegrationName.TRM_Sanctions}
                          value={trmSanctionsResult?.matches.length.toString() || "0"}
                          date={trmSanctionsResult.createdAt}
                          isHighAlert={(trmSanctionsResult?.matches.length || 0) > 0}
                          logo="/assets/trm.png"
                        />
                      ) : (
                        NO_ACTIVE_INTEGRATIONS
                      ),
                  }
                : {},
              isIncoming
                ? {
                    label: "Additional information",
                    mobile: 12,
                    tablet: 12,
                    desktop: 12,
                    value: (() => {
                      if (chainalysisOriginatorResponse) {
                        return (
                          <TooltipWrap>
                            <ClickableText
                              id="tooltip-reference"
                              ref={setPopperRefElementOriginatorAdd}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              {chainalysisAdditionalInfo(chainalysisOriginatorResponse)}
                              {<CheckCircle className="ml-2" />}
                            </ClickableText>

                            <Tooltip
                              id="tooltip-container"
                              ref={setPopperElementOriginatorAdd}
                              style={popperStylesOriginatorAdd.popper}
                              {...popperAttrsOriginatorAdd.popper}
                            >
                              <IntegrationTooltipContent
                                logoUrl="/assets/chainalysis.png"
                                integrationName={IntegrationName.Chainalysis_V1}
                              />
                              <div
                                id="arrow"
                                ref={setPopperArrowElementOriginatorAdd}
                                style={popperStylesOriginatorAdd.arrow}
                              />
                            </Tooltip>
                          </TooltipWrap>
                        );
                      }

                      if (chainalysis2OriginatorResponse) {
                        return (
                          <TooltipWrap>
                            <ClickableText
                              id="tooltip-reference"
                              ref={setPopperRefElementOriginatorAdd}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              {chainalysisAdditionalInfo(chainalysis2OriginatorResponse)}
                              {<CheckCircle className="ml-2" />}
                            </ClickableText>

                            <Tooltip
                              id="tooltip-container"
                              ref={setPopperElementOriginatorAdd}
                              style={popperStylesOriginatorAdd.popper}
                              {...popperAttrsOriginatorAdd.popper}
                            >
                              <IntegrationTooltipContent
                                logoUrl="/assets/chainalysis.png"
                                integrationName={IntegrationName.Chainalysis_V2}
                              />
                              <div
                                id="arrow"
                                ref={setPopperArrowElementOriginatorAdd}
                                style={popperStylesOriginatorAdd.arrow}
                              />
                            </Tooltip>
                          </TooltipWrap>
                        );
                      }

                      if (coinfirmOriginatorResponse) {
                        return (
                          <TooltipWrap>
                            <ClickableText
                              id="tooltip-reference"
                              ref={setPopperRefElementOriginatorAdd}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              {coinfirmAdditionalInfo(coinfirmOriginatorResponse?.profile_section?.owner)}
                              {<CheckCircle className="ml-2" />}
                            </ClickableText>

                            <Tooltip
                              id="tooltip-container"
                              ref={setPopperElementOriginatorAdd}
                              style={popperStylesOriginatorAdd.popper}
                              {...popperAttrsOriginatorAdd.popper}
                            >
                              <IntegrationTooltipContent
                                logoUrl="/assets/coinfirm.png"
                                integrationName={IntegrationName.Coinfirm}
                              />
                              <div
                                id="arrow"
                                ref={setPopperArrowElementOriginatorAdd}
                                style={popperStylesOriginatorAdd.arrow}
                              />
                            </Tooltip>
                          </TooltipWrap>
                        );
                      }

                      if (crystalOriginatorResponse) {
                        return (
                          <TooltipWrap>
                            <ClickableText
                              id="tooltip-reference"
                              ref={setPopperRefElementOriginatorAdd}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              {crystalOriginatorResponse?.data?.counterparty?.name}
                              {<CheckCircle className="ml-2" />}
                            </ClickableText>

                            <Tooltip
                              id="tooltip-container"
                              ref={setPopperElementOriginatorAdd}
                              style={popperStylesOriginatorAdd.popper}
                              {...popperAttrsOriginatorAdd.popper}
                            >
                              <IntegrationTooltipContent
                                logoUrl="/assets/crystalBlockchain.svg"
                                integrationName={IntegrationName.Crystal}
                              />
                              <div
                                id="arrow"
                                ref={setPopperArrowElementOriginatorAdd}
                                style={popperStylesOriginatorAdd.arrow}
                              />
                            </Tooltip>
                          </TooltipWrap>
                        );
                      }

                      if (merkleScienceOriginatorResponse) {
                        return (
                          <TooltipWrap>
                            <ClickableText
                              id="tooltip-reference"
                              ref={setPopperRefElementOriginatorAdd}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              {merkleScienceAdditionaInfo(merkleScienceOriginatorResponse?.tags?.owner)}
                              {<CheckCircle className="ml-2" />}
                            </ClickableText>

                            <Tooltip
                              id="tooltip-container"
                              ref={setPopperElementOriginatorAdd}
                              style={popperStylesOriginatorAdd.popper}
                              {...popperAttrsOriginatorAdd.popper}
                            >
                              <IntegrationTooltipContent
                                logoUrl="/assets/merkleScience.png"
                                integrationName={IntegrationName.Merkle_Science}
                              />
                              <div
                                id="arrow"
                                ref={setPopperArrowElementOriginatorAdd}
                                style={popperStylesOriginatorAdd.arrow}
                              />
                            </Tooltip>
                          </TooltipWrap>
                        );
                      }

                      if (ellipticOriginatorResponse) {
                        return (
                          <TooltipWrap>
                            <ClickableText
                              id="tooltip-reference"
                              ref={setPopperRefElementOriginatorAdd}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              {ellipticAdditionalInfo(ellipticOriginatorResponse)}
                              {<CheckCircle className="ml-2" />}
                            </ClickableText>

                            <Tooltip
                              id="tooltip-container"
                              ref={setPopperElementOriginatorAdd}
                              style={popperStylesOriginatorAdd.popper}
                              {...popperAttrsOriginatorAdd.popper}
                            >
                              <IntegrationTooltipContent
                                logoUrl="/assets/elliptic.png"
                                integrationName={IntegrationName.Elliptic}
                              />
                              <div
                                id="arrow"
                                ref={setPopperArrowElementOriginatorAdd}
                                style={popperStylesOriginatorAdd.arrow}
                              />
                            </Tooltip>
                          </TooltipWrap>
                        );
                      }

                      // if (scorechainOriginatorResponse) {
                      //   return (
                      //     <TooltipWrap>
                      //       <ClickableText
                      //         id="tooltip-reference"
                      //         ref={setPopperRefElementOriginatorAdd}
                      //         style={{
                      //           display: "inline-flex",
                      //           alignItems: "center",
                      //         }}
                      //       >
                      //         {JSON.stringify(scorechainOriginatorResponse)}
                      //         {<CheckCircle className="ml-2" />}
                      //       </ClickableText>

                      //       <Tooltip
                      //         id="tooltip-container"
                      //         ref={setPopperElementOriginatorAdd}
                      //         style={popperStylesOriginatorAdd.popper}
                      //         {...popperAttrsOriginatorAdd.popper}
                      //       >
                      //         <IntegrationTooltipContent
                      //           logoUrl="/assets/scorechain.png"
                      //           integrationName={IntegrationName.Scorechain}
                      //         />
                      //       </Tooltip>
                      //     </TooltipWrap>
                      //   );
                      // }

                      return EMPTY;
                    })(),
                  }
                : {},
              {
                label: "To Blockchain address",
                value: transaction.beneficiaryBlockchainAddress || EMPTY,
                warning: "beneficiaryAccountNumberMissing",
              },
              isOutgoing
                ? {
                    label: "Risk score Result",
                    value: beneficiaryHasRisk
                      ? activeIntegrationsBeneficiary.map((integration, index) => (
                          <>
                            <IntegrationResult
                              key={index}
                              longLabel="TRANSACTION RISK"
                              provider={integration.name}
                              value={integration.riskDisplayText}
                              date={integration.riskDate}
                              isHighAlert={
                                integration.riskDisplayText === "HIGH" || integration.riskDisplayText === "SEVERE"
                              }
                              logo={integration.logoUrl}
                            />
                          </>
                        ))
                      : NO_ACTIVE_INTEGRATIONS,
                  }
                : {},
              isOutgoing && activeIntegrationsErrorsBeneficiary && activeIntegrationsErrorsBeneficiary.length
                ? {
                    label: "Risk score Errors",
                    value: (
                      <IntegrationErrorsContainer>
                        {activeIntegrationsErrorsBeneficiary.map((integration, index) => (
                          <IntegrationResult
                            key={index}
                            longLabel="TRANSACTION RISK ERRORS"
                            provider={integration.name}
                            value={integration.riskDisplayText}
                            date={integration.riskDate}
                            isHighAlert={
                              integration.riskDisplayText === "HIGH" || integration.riskDisplayText === "SEVERE"
                            }
                            logo={integration.logoUrl}
                          />
                        ))}
                      </IntegrationErrorsContainer>
                    ),
                  }
                : {},
              isOutgoing
                ? {
                    label: "Counterparty Address Sanctioned?",
                    value:
                      hasTRMSanctions && trmSanctionsResult ? (
                        <IntegrationResult
                          shortLabel="MATCHES"
                          longLabel="SANCTION MATCHES"
                          provider={IntegrationName.TRM_Sanctions}
                          value={trmSanctionsResult?.matches.length.toString() || "0"}
                          date={trmSanctionsResult?.createdAt}
                          isHighAlert={(trmSanctionsResult?.matches.length || 0) > 0}
                          logo="/assets/trm.png"
                        />
                      ) : (
                        NO_ACTIVE_INTEGRATIONS
                      ),
                  }
                : {},
              isOutgoing
                ? {
                    label: "Additional information",
                    mobile: 12,
                    tablet: 12,
                    desktop: 12,
                    value: (() => {
                      if (chainalysisBeneficiaryResponse) {
                        return (
                          <TooltipWrap>
                            <ClickableText
                              id="tooltip-reference"
                              ref={setPopperRefElementBeneficiaryAdd}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              {chainalysisAdditionalInfo(chainalysisBeneficiaryResponse)}
                              {<CheckCircle className="ml-2" />}
                            </ClickableText>

                            <Tooltip
                              id="tooltip-container"
                              ref={setPopperElementBeneficiaryAdd}
                              style={popperStylesBeneficiaryAdd.popper}
                              {...popperAttrsBeneficiaryAdd.popper}
                            >
                              <IntegrationTooltipContent
                                logoUrl="/assets/chainalysis.png"
                                integrationName={IntegrationName.Chainalysis_V1}
                              />
                              <div
                                id="arrow"
                                ref={setPopperArrowElementBeneficiaryAdd}
                                style={popperStylesBeneficiaryAdd.arrow}
                              />
                            </Tooltip>
                          </TooltipWrap>
                        );
                      }

                      if (chainalysis2BeneficiaryResponse) {
                        return (
                          <TooltipWrap>
                            <ClickableText
                              id="tooltip-reference"
                              ref={setPopperRefElementBeneficiaryAdd}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              {chainalysisAdditionalInfo(chainalysis2BeneficiaryResponse)}
                              {<CheckCircle className="ml-2" />}
                            </ClickableText>

                            <Tooltip
                              id="tooltip-container"
                              ref={setPopperElementBeneficiaryAdd}
                              style={popperStylesBeneficiaryAdd.popper}
                              {...popperAttrsBeneficiaryAdd.popper}
                            >
                              <IntegrationTooltipContent
                                logoUrl="/assets/chainalysis.png"
                                integrationName={IntegrationName.Chainalysis_V2}
                              />
                              <div
                                id="arrow"
                                ref={setPopperArrowElementBeneficiaryAdd}
                                style={popperStylesBeneficiaryAdd.arrow}
                              />
                            </Tooltip>
                          </TooltipWrap>
                        );
                      }

                      if (coinfirmBeneficiaryResponse) {
                        return (
                          <TooltipWrap>
                            <ClickableText
                              id="tooltip-reference"
                              ref={setPopperRefElementBeneficiaryAdd}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              {coinfirmAdditionalInfo(coinfirmBeneficiaryResponse?.profile_section?.owner)}
                              {<CheckCircle className="ml-2" />}
                            </ClickableText>

                            <Tooltip
                              id="tooltip-container"
                              ref={setPopperElementBeneficiaryAdd}
                              style={popperStylesBeneficiaryAdd.popper}
                              {...popperAttrsBeneficiaryAdd.popper}
                            >
                              <IntegrationTooltipContent
                                logoUrl="/assets/coinfirm.png"
                                integrationName={IntegrationName.Coinfirm}
                              />
                              <div
                                id="arrow"
                                ref={setPopperArrowElementBeneficiaryAdd}
                                style={popperStylesBeneficiaryAdd.arrow}
                              />
                            </Tooltip>
                          </TooltipWrap>
                        );
                      }

                      if (crystalBeneficiaryResponse) {
                        return (
                          <TooltipWrap>
                            <ClickableText
                              id="tooltip-reference"
                              ref={setPopperRefElementBeneficiaryAdd}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              {crystalBeneficiaryResponse?.data?.counterparty?.name}
                              {<CheckCircle className="ml-2" />}
                            </ClickableText>

                            <Tooltip
                              id="tooltip-container"
                              ref={setPopperElementBeneficiaryAdd}
                              style={popperStylesBeneficiaryAdd.popper}
                              {...popperAttrsBeneficiaryAdd.popper}
                            >
                              <IntegrationTooltipContent
                                logoUrl="/assets/crystalBlockchain.svg"
                                integrationName={IntegrationName.Crystal}
                              />
                              <div
                                id="arrow"
                                ref={setPopperArrowElementBeneficiaryAdd}
                                style={popperStylesBeneficiaryAdd.arrow}
                              />
                            </Tooltip>
                          </TooltipWrap>
                        );
                      }

                      if (merkleScienceBeneficiaryResponse) {
                        return (
                          <TooltipWrap>
                            <ClickableText
                              id="tooltip-reference"
                              ref={setPopperRefElementBeneficiaryAdd}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              {merkleScienceAdditionaInfo(merkleScienceBeneficiaryResponse?.tags?.owner)}
                              {<CheckCircle className="ml-2" />}
                            </ClickableText>

                            <Tooltip
                              id="tooltip-container"
                              ref={setPopperElementBeneficiaryAdd}
                              style={popperStylesBeneficiaryAdd.popper}
                              {...popperAttrsBeneficiaryAdd.popper}
                            >
                              <IntegrationTooltipContent
                                logoUrl="/assets/merkleScience.png"
                                integrationName={IntegrationName.Merkle_Science}
                              />
                              <div
                                id="arrow"
                                ref={setPopperArrowElementOriginatorAdd}
                                style={popperStylesBeneficiaryAdd.arrow}
                              />
                            </Tooltip>
                          </TooltipWrap>
                        );
                      }

                      if (ellipticBeneficiaryResponse) {
                        return (
                          <TooltipWrap>
                            <ClickableText
                              id="tooltip-reference"
                              ref={setPopperRefElementBeneficiaryAdd}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              {ellipticAdditionalInfo(ellipticBeneficiaryResponse)}
                              {<CheckCircle className="ml-2" />}
                            </ClickableText>

                            <Tooltip
                              id="tooltip-container"
                              ref={setPopperElementBeneficiaryAdd}
                              style={popperStylesBeneficiaryAdd.popper}
                              {...popperAttrsBeneficiaryAdd.popper}
                            >
                              <IntegrationTooltipContent
                                logoUrl="/assets/elliptic.png"
                                integrationName={IntegrationName.Elliptic}
                              />
                              <div
                                id="arrow"
                                ref={setPopperArrowElementOriginatorAdd}
                                style={popperStylesBeneficiaryAdd.arrow}
                              />
                            </Tooltip>
                          </TooltipWrap>
                        );
                      }

                      // if (scorechainBeneficiaryResponse) {
                      //   return (
                      //     <TooltipWrap>
                      //       <ClickableText
                      //         id="tooltip-reference"
                      //         ref={setPopperRefElementBeneficiaryAdd}
                      //         style={{
                      //           display: "inline-flex",
                      //           alignItems: "center",
                      //         }}
                      //       >
                      //         {JSON.stringify(scorechainBeneficiaryResponse)}
                      //         {<CheckCircle className="ml-2" />}
                      //       </ClickableText>

                      //       <Tooltip
                      //         id="tooltip-container"
                      //         ref={setPopperElementBeneficiaryAdd}
                      //         style={popperStylesBeneficiaryAdd.popper}
                      //         {...popperAttrsBeneficiaryAdd.popper}
                      //       >
                      //         <IntegrationTooltipContent
                      //           logoUrl="/assets/scorechain.png"
                      //           integrationName={IntegrationName.Scorechain}
                      //         />
                      //       </Tooltip>
                      //     </TooltipWrap>
                      //   );
                      // }

                      return EMPTY;
                    })(),
                  }
                : { mobile: 0, tablet: 0, desktop: 0 },
            ].map(({ label, value, mobile = 12, tablet = 6, desktop = 6, warning }) => {
              if (value === EMPTY && warning && warnings && warnings.includes(warning))
                value = <WarningLabel>required in counterparty&apos;s jurisdiction</WarningLabel>;
              const optionalLabel =
                warning && warnings && warnings.includes(warning) ? (
                  <OptionalLabel title="This data is required in your counterparty VASP's jurisdiction but not in yours.">
                    optional&nbsp;&#128712;
                  </OptionalLabel>
                ) : undefined;
              return (
                <DetailList.Item key={label} mobile={mobile} tablet={tablet} desktop={desktop}>
                  <DetailList.Term id={label}>
                    {label} {optionalLabel}
                  </DetailList.Term>
                  <DetailList.Description
                    style={{ display: "flex", columnGap: "3px" }}
                    id={`${label}-value`}
                    className="localize-ignore"
                  >
                    {value}
                  </DetailList.Description>
                </DetailList.Item>
              );
            })}
            {transaction.typeIsNonCustodial && transaction.beneficiaryProof && showOwnershipProof && (
              <DetailList.Item key="ownership-detail" mobile={12} tablet={12} desktop={6}>
                <DetailList.Term>OWNERSHIP PROOF</DetailList.Term>
                <DetailList.Description className="localize-ignore">
                  <OwnershipProof transaction={transaction} />
                  <OwnershipProofText color="#8E8E8E" display="block">
                    {formatterDateTime.format(transaction.createdAt)}
                  </OwnershipProofText>
                </DetailList.Description>
              </DetailList.Item>
            )}
          </DetailList.Content>
        ) : (
          <SkeletonDetailList count={5} />
        )}
      </Padding>
    </Box>
  );
}

const ProofImageLoader = ({ s3Key }: { s3Key: string }) => {
  return (
    <>
      <span>The beneficiary proof is an image attached to the transfer</span>
      <Button
        style={{ margin: "10px 0" }}
        kind="primary"
        onClick={() => {
          getImageUrlFromS3Key(s3Key)
            .then(({ presignedUrl }) => {
              window.open(presignedUrl, "_blank");
            })
            .catch(() => {
              alert("There was a problem fetching the image. Please try again later or contact Notabene.");
            });
        }}
      >
        View image
      </Button>
    </>
  );
};
